import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingPopup from '../../SharedView/LoadingPopup';
import './editItem.css';

function EditItem({ item, isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '', default_price: '', is_addon: false,
    zoho_item_id: '', property_id: '', is_laundry: false,
    is_client_addon: false, client_display_name: '',
  });
  const [zohoName, setZohoName] = useState('');
  const [zohoLoading, setZohoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        default_price: item.default_price || '',
        is_addon: item.is_addon || false,
        zoho_item_id: item.zoho_item_id || '',
        property_id: item.property_id || '',
        is_laundry: item.is_laundry || false,
        is_client_addon: item.is_client_addon || false,
        // Pre-fill from the saved client name, else the item name (2026-07-20).
        client_display_name: item.client_display_name || item.name || '',
      });

      // Fetch Zoho display name if item has a zoho_item_id
      if (item.zoho_item_id) {
        setZohoLoading(true);
        axios.get(`/api/items/${item.id}/zoho`)
          .then(res => {
            setZohoName(res.data.zoho_name || '');
          })
          .catch(() => {
            setZohoName('');
          })
          .finally(() => setZohoLoading(false));
      } else {
        setZohoName('');
      }
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`/api/items/${item.id}`, {
        ...formData,
        zoho_name: zohoName || undefined,
      });
      if (response.status === 200) {
        onSave(response.data.item);
        setSaving(false);
        setFeedbackMessage('Item updated successfully.');
      }
    } catch (err) {
      console.error('Error updating item:', err);
      setSaving(false);
      setFeedbackMessage(err.response?.data?.message || 'Failed to update item.');
    }
  };

  // Retroactively create a Zoho Books item from this local item and link
  // it. Used for items created before Zoho integration (e.g. the
  // auto-inserted "Maintenance Service" item) — saves the user from having
  // to delete + re-create via AddItem just to get a Zoho linkage.
  const handleSyncToZoho = async () => {
    const displayName = window.prompt(
      'Name to use for this item in Zoho Books:',
      formData.name
    );
    if (!displayName || !displayName.trim()) return;
    setSaving(true);
    try {
      const response = await axios.post(`/api/items/${item.id}/sync-to-zoho`, {
        zoho_display_name: displayName.trim(),
      });
      if (response.status === 200) {
        const updated = response.data.item;
        setFormData(prev => ({ ...prev, zoho_item_id: updated.zoho_item_id || '' }));
        setZohoName(displayName.trim());
        onSave(updated);
        setFeedbackMessage('Item created in Zoho and linked.');
      }
    } catch (err) {
      console.error('Error syncing item to Zoho:', err);
      setFeedbackMessage(err.response?.data?.message || 'Failed to sync item to Zoho.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="itemModal__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="itemModal">
        <button type="button" className="modal-x" onClick={onClose}>&times;</button>
        <h2 className="itemModal__title">Edit Item</h2>
        <p className="itemModal__subtitle">#{String(item.id).padStart(5, '0')}</p>

        <form onSubmit={handleSubmit} className="itemModal__form">
          <div className="itemModal__group">
            <label className="itemModal__label">Portal Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} className="itemModal__input" required />
          </div>

          {/* Zoho name — fetched from Zoho, editable separately */}
          {formData.zoho_item_id && (
            <div className="itemModal__group">
              <label className="itemModal__label">
                Zoho Display Name
                {zohoLoading && <span className="itemModal__hint"> — loading from Zoho...</span>}
              </label>
              <input
                type="text"
                value={zohoName}
                onChange={(e) => setZohoName(e.target.value)}
                className="itemModal__input itemModal__input--zoho"
                placeholder={zohoLoading ? 'Loading...' : 'Not set in Zoho'}
                disabled={zohoLoading}
              />
            </div>
          )}

          <div className="itemModal__row">
            <div className="itemModal__group">
              <label className="itemModal__label">Default Price (AED)</label>
              <input type="number" name="default_price" value={formData.default_price} onChange={handleChange} step="0.01" className="itemModal__input" required />
            </div>
            <div className="itemModal__group">
              <label className="itemModal__label">Property ID</label>
              <input type="number" name="property_id" value={formData.property_id} onChange={handleChange} className="itemModal__input" placeholder="Optional" />
            </div>
          </div>

          <div className="itemModal__group">
            <label className="itemModal__label">Zoho Item ID</label>
            <input type="text" name="zoho_item_id" value={formData.zoho_item_id} onChange={handleChange} className="itemModal__input" placeholder="Optional" />
            {!formData.zoho_item_id && (
              <button
                type="button"
                onClick={handleSyncToZoho}
                disabled={saving}
                className="itemModal__btn itemModal__btn--ghost"
                style={{ marginTop: 8, width: '100%' }}
              >
                {saving ? 'Creating in Zoho...' : 'Create & link to Zoho Books'}
              </button>
            )}
          </div>

          <div className="itemModal__toggles">
            <label className="itemModal__toggle">
              <input type="checkbox" name="is_addon" checked={formData.is_addon} onChange={handleChange} />
              <span>Add-on</span>
            </label>
            <label className="itemModal__toggle">
              <input type="checkbox" name="is_laundry" checked={formData.is_laundry} onChange={handleChange} />
              <span>Laundry</span>
            </label>
            <label className="itemModal__toggle">
              <input type="checkbox" name="is_client_addon" checked={formData.is_client_addon} onChange={handleChange} />
              <span>Extra linen (client-selectable)</span>
            </label>
          </div>

          {formData.is_client_addon && (
            <div className="itemModal__group">
              <label className="itemModal__label">Client display name</label>
              <input
                type="text"
                name="client_display_name"
                value={formData.client_display_name}
                onChange={handleChange}
                className="itemModal__input"
                placeholder="Name shown to clients"
              />
              <span className="itemModal__hint">Shown to clients when they add this. Pre-filled from the item name — edit if you want something different.</span>
            </div>
          )}

          <div className="itemModal__actions">
            <button type="button" onClick={onClose} className="itemModal__btn itemModal__btn--ghost">Cancel</button>
            <button type="submit" className="itemModal__btn itemModal__btn--primary" disabled={saving || zohoLoading}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
      <LoadingPopup isLoading={saving} feedbackMessage={feedbackMessage} onClose={() => { setFeedbackMessage(''); if (!saving) onClose(); }} />
    </div>
  );
}

export default EditItem;
